import { useRef, useState } from "react";
import Seo from "@/components/Seo";
import Turnstile, { type TurnstileHandle } from "@/components/Turnstile";

const ACCENT = "#0E2644";
const BG = "#F5F6F8";

type Question =
  | { id: string; type: "mc"; text: string; options: string[]; helper?: string }
  | { id: string; type: "scale"; text: string; helper?: string }
  | { id: string; type: "text"; text: string; helper?: string };

interface Section {
  title: string;
  questions: Question[];
}

const SECTIONS: Section[] = [
  {
    title: "Communication & Management",
    questions: [
      {
        id: "q1",
        type: "mc",
        text: "Do you wish you met with Luke more often, less often, or is the current frequency right?",
        options: ["More often", "Less often", "About right"],
      },
      {
        id: "q2",
        type: "scale",
        text: "When you have a question or blocker, how quickly do you typically get an answer?",
        helper: "1 = very slow, 10 = very fast",
      },
      {
        id: "q3",
        type: "scale",
        text: "Do you feel like you know what's expected of you day to day?",
        helper: "1 = not at all clear, 10 = completely clear",
      },
      {
        id: "q4",
        type: "text",
        text: "Is there anything Luke does in meetings or messages that makes it harder for you to do your job?",
      },
    ],
  },
  {
    title: "Growth & Engagement",
    questions: [
      {
        id: "q5",
        type: "scale",
        text: "Do you feel like you're learning or growing in this role?",
        helper: "1 = not at all, 10 = significantly",
      },
      { id: "q6", type: "text", text: "Is there a skill or type of work you'd like to do more of?" },
      { id: "q7", type: "text", text: "Where do you see yourself in this role a year from now?" },
    ],
  },
  {
    title: "Culture & Trust",
    questions: [
      {
        id: "q8",
        type: "scale",
        text: "Do you feel comfortable telling Luke when something isn't working?",
        helper: "1 = not at all comfortable, 10 = completely comfortable",
      },
      {
        id: "q9",
        type: "scale",
        text: "Do you feel recognized when you do good work?",
        helper: "1 = never, 10 = always",
      },
      { id: "q10", type: "text", text: "How would you describe working at Blue Heron to a friend?" },
    ],
  },
  {
    title: "Compensation & Stability",
    questions: [
      {
        id: "q11",
        type: "mc",
        text: "Do you feel your pay is fair for the work you're doing?",
        options: ["Yes", "No", "Somewhat"],
      },
      { id: "q12", type: "text", text: "Do you have concerns about job security or the direction of the company?" },
    ],
  },
  {
    title: "Open-ended",
    questions: [
      { id: "q13", type: "text", text: "What's one thing Luke could change that would make your work life better?" },
      { id: "q14", type: "text", text: "What's one thing you'd never want changed?" },
    ],
  },
];

const REQUIRED_IDS = SECTIONS.flatMap((s) => s.questions).filter((q) => q.type !== "text").map((q) => q.id);

const labelStyle = { color: ACCENT };
const cardStyle = { border: "1px solid rgba(14,38,68,.1)" };

const Survey = () => {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [missingId, setMissingId] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState(false);
  const turnstileRef = useRef<TurnstileHandle>(null);

  const setAnswer = (id: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    if (id === missingId) {
      setMissingId(null);
      setError(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const firstMissing = REQUIRED_IDS.find((id) => !answers[id]);
    if (firstMissing) {
      setError(true);
      setMissingId(firstMissing);
      document.getElementById(`q-${firstMissing}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setError(false);
    setMissingId(null);
    if (!turnstileToken) {
      setVerifyError(true);
      return;
    }
    setVerifyError(false);
    setSubmitting(true);

    const responses = SECTIONS.flatMap((section) =>
      section.questions.map((q) => ({
        section: section.title,
        question: q.text,
        answer: answers[q.id] || "(no answer)",
      }))
    );

    try {
      const res = await fetch("/api/submit-survey", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ responses, submittedAt: new Date().toISOString(), turnstileToken }),
      });
      if (res.status === 403) {
        setVerifyError(true);
        turnstileRef.current?.reset();
        return;
      }
      if (!res.ok) throw new Error(`Submit failed with status ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      console.error("Survey submit error:", err);
      setError(true);
      turnstileRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: BG, fontFamily: "'Archivo', sans-serif" }}>
        <Seo title="Thank You | Team Survey" description="Survey response submitted." path="/survey" noindex />
        <div className="max-w-[480px] text-center">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6 font-black text-2xl text-white"
            style={{ background: ACCENT }}
          >
            ✓
          </div>
          <h1 className="font-extrabold text-2xl mb-3" style={{ color: ACCENT }}>
            Thanks, your response has been submitted.
          </h1>
          <p className="leading-relaxed" style={{ color: "rgba(14,38,68,0.6)" }}>
            Your feedback is anonymous and appreciated.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 py-14 md:py-20" style={{ background: BG, fontFamily: "'Archivo', sans-serif" }}>
      <Seo title="Team Survey" description="Anonymous internal team feedback survey." path="/survey" noindex />
      <div className="max-w-[640px] mx-auto">
        <div className="mb-10">
          <h1 className="font-extrabold text-3xl mb-3" style={{ color: ACCENT }}>
            Team Feedback Survey
          </h1>
          <p className="leading-relaxed" style={{ color: "rgba(14,38,68,0.65)" }}>
            This survey is completely anonymous. No name, email, or identifying information is collected, only
            your answers below.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {SECTIONS.map((section) => (
            <div key={section.title} className="mb-9">
              <h2 className="font-bold text-sm tracking-[0.08em] uppercase mb-4" style={{ color: "rgba(14,38,68,0.45)" }}>
                {section.title}
              </h2>
              <div className="flex flex-col gap-4">
                {section.questions.map((q) => (
                  <div
                    key={q.id}
                    id={`q-${q.id}`}
                    className="bg-white rounded-2xl p-6 scroll-mt-6 transition-[box-shadow,border-color]"
                    style={
                      missingId === q.id
                        ? { border: "1px solid #b3261e", boxShadow: "0 0 0 3px rgba(179,38,30,.12)" }
                        : cardStyle
                    }
                  >
                    <label className="block font-semibold text-[15px] mb-1" style={labelStyle}>
                      {q.text}
                    </label>
                    {q.helper && (
                      <p className="text-[13px] mb-3" style={{ color: "rgba(14,38,68,0.5)" }}>
                        {q.helper}
                      </p>
                    )}
                    {missingId === q.id && (
                      <p className="text-[13px] font-semibold mb-1" style={{ color: "#b3261e" }}>
                        Please answer this question.
                      </p>
                    )}

                    {q.type === "mc" && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {q.options.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setAnswer(q.id, opt)}
                            className="px-4 py-2.5 rounded-full font-semibold text-sm border cursor-pointer transition-colors"
                            style={
                              answers[q.id] === opt
                                ? { background: ACCENT, color: "#fff", borderColor: ACCENT }
                                : { background: "transparent", color: ACCENT, borderColor: "rgba(14,38,68,.25)" }
                            }
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}

                    {q.type === "scale" && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {Array.from({ length: 10 }, (_, i) => String(i + 1)).map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setAnswer(q.id, n)}
                            className="w-9 h-9 rounded-full font-bold text-[13px] border cursor-pointer transition-colors shrink-0"
                            style={
                              answers[q.id] === n
                                ? { background: ACCENT, color: "#fff", borderColor: ACCENT }
                                : { background: "transparent", color: ACCENT, borderColor: "rgba(14,38,68,.25)" }
                            }
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    )}

                    {q.type === "text" && (
                      <textarea
                        rows={3}
                        value={answers[q.id] || ""}
                        onChange={(e) => setAnswer(q.id, e.target.value)}
                        placeholder="Your answer (optional)"
                        className="w-full box-border px-4 py-3 rounded-xl text-[15px] resize-y mt-1"
                        style={{ border: "1px solid rgba(14,38,68,.15)", background: "#FBFBFC", color: ACCENT, fontFamily: "inherit" }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <Turnstile
            ref={turnstileRef}
            onToken={(token) => {
              setTurnstileToken(token);
              if (token) setVerifyError(false);
            }}
            className="mb-4"
          />

          {verifyError && (
            <p className="text-sm font-semibold mb-4" style={{ color: "#b3261e" }}>
              Please complete the verification check above and try again.
            </p>
          )}

          {error && (
            <p className="text-sm font-semibold mb-4" style={{ color: "#b3261e" }}>
              {missingId
                ? "A required question above is missing an answer, jump back up to it."
                : "Something went wrong submitting your response. Please try again."}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 font-extrabold text-base py-4 rounded-full border-none cursor-pointer text-white transition-opacity disabled:opacity-60"
            style={{ background: ACCENT }}
          >
            {submitting ? "Submitting…" : "Submit Response"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Survey;
